import React, { useState, useEffect, useRef } from 'react';
import { Layout } from '../components/Layout';
import axios from 'axios';
import * as signalR from '@microsoft/signalr';

const API_BASE_URL = 'http://localhost:5100/api/strategy-subscriptions';
const HUB_URL = 'http://localhost:5100/hubs/strategy';

interface StrategySubscription {
  id: number;
  strategyName: string;
  enabled: boolean;
  broadcastOnPercentileChange: boolean;
  broadcastOnVolatilityChange: boolean;
  throttleMinutes: number;
  createdAt: string;
  updatedAt: string | null;
  createdBy: string | null;
  lastBroadcastAt: string | null;
  broadcastCount: number;
  notes: string | null;
  canBroadcastNow: boolean;
  nextBroadcastAvailableAt: string | null;
}

interface PercentileBroadcast {
  strategyName: string;
  pairName: string;
  timestamp: string;
  trigger: string;
  data: {
    p95: number;
    p5: number;
    volatilityRegime: string;
  };
}

const StrategySubscriptions: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<StrategySubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [recentBroadcasts, setRecentBroadcasts] = useState<PercentileBroadcast[]>([]);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected'>('disconnected');
  const connectionRef = useRef<signalR.HubConnection | null>(null);

  // Form state
  const [newStrategy, setNewStrategy] = useState({
    strategyName: '',
    broadcastOnPercentileChange: true,
    broadcastOnVolatilityChange: true,
    throttleMinutes: 5,
    notes: ''
  });

  // Fetch subscriptions
  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_BASE_URL);
      setSubscriptions(response.data.subscriptions || []);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch subscriptions');
      console.error('Error fetching subscriptions:', err);
    } finally {
      setLoading(false);
    }
  };

  // Add subscription
  const handleAddSubscription = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE_URL}/subscribe`, newStrategy);
      setShowAddForm(false);
      setNewStrategy({
        strategyName: '',
        broadcastOnPercentileChange: true,
        broadcastOnVolatilityChange: true,
        throttleMinutes: 5,
        notes: ''
      });
      fetchSubscriptions();
    } catch (err: any) {
      alert(`Error: ${err.response?.data?.message || err.message}`);
    }
  };

  // Toggle enabled status
  const handleToggleEnabled = async (subscription: StrategySubscription) => {
    try {
      if (subscription.enabled) {
        // Unsubscribe
        await axios.delete(`${API_BASE_URL}/unsubscribe/${subscription.strategyName}`);
      } else {
        // Re-enable
        await axios.put(`${API_BASE_URL}/${subscription.strategyName}`, {
          strategyName: subscription.strategyName,
          enabled: true,
          broadcastOnPercentileChange: subscription.broadcastOnPercentileChange,
          broadcastOnVolatilityChange: subscription.broadcastOnVolatilityChange,
          throttleMinutes: subscription.throttleMinutes,
          notes: subscription.notes
        });
      }
      fetchSubscriptions();
    } catch (err: any) {
      alert(`Error: ${err.response?.data?.message || err.message}`);
    }
  };

  // SignalR connection setup for real-time broadcasts
  useEffect(() => {
    const setupSignalR = async () => {
      try {
        setConnectionStatus('connecting');

        const connection = new signalR.HubConnectionBuilder()
          .withUrl(HUB_URL)
          .withAutomaticReconnect()
          .configureLogging(signalR.LogLevel.Information)
          .build();

        // Handle percentile broadcasts
        connection.on('PercentileBroadcast', (broadcast: PercentileBroadcast) => {
          console.log('[SignalR] Percentile broadcast received:', broadcast);

          // Add to recent broadcasts (keep last 20)
          setRecentBroadcasts(prev => [broadcast, ...prev].slice(0, 20));

          // Optionally refresh subscriptions to update broadcast counts
          fetchSubscriptions();
        });

        // Handle admin monitor confirmation
        connection.on('AdminMonitorConfirmed', (data: any) => {
          console.log('[SignalR] Admin monitor confirmed:', data);
        });

        // Handle connection state changes
        connection.onreconnecting(() => {
          console.log('[SignalR] Reconnecting...');
          setConnectionStatus('connecting');
        });

        connection.onreconnected(() => {
          console.log('[SignalR] Reconnected');
          setConnectionStatus('connected');
        });

        connection.onclose(() => {
          console.log('[SignalR] Connection closed');
          setConnectionStatus('disconnected');
        });

        // Start connection
        await connection.start();
        console.log('[SignalR] Connected to StrategyHub');
        setConnectionStatus('connected');

        // Subscribe to admin monitoring group
        await connection.invoke('SubscribeToAdminMonitor');
        console.log('[SignalR] Subscribed to admin monitoring');

        connectionRef.current = connection;
      } catch (err: any) {
        console.error('[SignalR] Connection error:', err);
        setConnectionStatus('disconnected');
      }
    };

    setupSignalR();

    // Cleanup on unmount
    return () => {
      if (connectionRef.current) {
        connectionRef.current.stop();
        console.log('[SignalR] Connection stopped');
      }
    };
  }, []);

  useEffect(() => {
    fetchSubscriptions();
    const interval = setInterval(fetchSubscriptions, 10000); // Refresh every 10 seconds
    return () => clearInterval(interval);
  }, []);

  // Format timestamp
  const formatTimestamp = (timestamp: string | null) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleString();
  };

  // Calculate next broadcast time
  const getNextBroadcastTime = (subscription: StrategySubscription) => {
    if (!subscription.lastBroadcastAt) return 'Ready';
    if (subscription.canBroadcastNow) return 'Ready';
    if (subscription.nextBroadcastAvailableAt) {
      const next = new Date(subscription.nextBroadcastAvailableAt);
      const now = new Date();
      const diffMinutes = Math.ceil((next.getTime() - now.getTime()) / 60000);
      return `${diffMinutes}min`;
    }
    return 'Unknown';
  };

  return (
    <Layout>
      <div style={{ padding: '20px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px'
        }}>
          <div>
            <h1 style={{ fontSize: '32px', marginBottom: '10px', color: '#f1f5f9' }}>
              📡 Strategy Subscriptions
            </h1>
            <p style={{ color: '#94a3b8', fontSize: '14px' }}>
              Manage real-time percentile broadcasts to StrategyManager instances
            </p>
          </div>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            style={{
              padding: '12px 24px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
          >
            {showAddForm ? '✖ Cancel' : '+ Add Subscription'}
          </button>
        </div>

        {/* SignalR Connection Status */}
        <div style={{
          padding: '12px 20px',
          backgroundColor: connectionStatus === 'connected' ? '#064e3b' : connectionStatus === 'connecting' ? '#78350f' : '#7f1d1d',
          borderRadius: '8px',
          marginBottom: '20px',
          border: `1px solid ${connectionStatus === 'connected' ? '#065f46' : connectionStatus === 'connecting' ? '#92400e' : '#991b1b'}`,
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <span style={{ fontSize: '20px' }}>
            {connectionStatus === 'connected' ? '🟢' : connectionStatus === 'connecting' ? '🟡' : '🔴'}
          </span>
          <span style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: 'bold' }}>
            SignalR: {connectionStatus.toUpperCase()}
          </span>
          {recentBroadcasts.length > 0 && (
            <span style={{ color: '#94a3b8', fontSize: '12px', marginLeft: 'auto' }}>
              {recentBroadcasts.length} broadcasts received
            </span>
          )}
        </div>

        {/* Recent Broadcasts */}
        {recentBroadcasts.length > 0 && (
          <div style={{
            backgroundColor: '#1e293b',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px',
            border: '1px solid #334155'
          }}>
            <h2 style={{ fontSize: '18px', marginBottom: '16px', color: '#f1f5f9' }}>
              Recent Broadcasts
            </h2>
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {recentBroadcasts.map((broadcast, index) => (
                <div
                  key={index}
                  style={{
                    padding: '12px',
                    backgroundColor: '#0f172a',
                    borderRadius: '6px',
                    marginBottom: '8px',
                    fontSize: '12px',
                    color: '#cbd5e1'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>{broadcast.strategyName}</span>
                    <span style={{ color: '#64748b' }}>{new Date(broadcast.timestamp).toLocaleTimeString()}</span>
                  </div>
                  <div style={{ color: '#94a3b8' }}>
                    {broadcast.trigger} | P95: {broadcast.data.p95.toFixed(5)} | P5: {broadcast.data.p5.toFixed(5)} | {broadcast.data.volatilityRegime}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add Subscription Form */}
        {showAddForm && (
          <div style={{
            backgroundColor: '#1e293b',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            border: '1px solid #334155'
          }}>
            <h2 style={{ fontSize: '20px', marginBottom: '20px', color: '#f1f5f9' }}>
              Add New Subscription
            </h2>
            <form onSubmit={handleAddSubscription}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#94a3b8', fontSize: '14px' }}>
                    Strategy Name
                  </label>
                  <input
                    type="text"
                    required
                    value={newStrategy.strategyName}
                    onChange={(e) => setNewStrategy({ ...newStrategy, strategyName: e.target.value })}
                    placeholder="e.g., LEAD_LAG"
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: '#0f172a',
                      border: '1px solid #334155',
                      borderRadius: '6px',
                      color: '#f1f5f9',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#94a3b8', fontSize: '14px' }}>
                    Throttle Minutes
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="60"
                    required
                    value={newStrategy.throttleMinutes}
                    onChange={(e) => setNewStrategy({ ...newStrategy, throttleMinutes: parseInt(e.target.value) })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: '#0f172a',
                      border: '1px solid #334155',
                      borderRadius: '6px',
                      color: '#f1f5f9',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', marginBottom: '10px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={newStrategy.broadcastOnPercentileChange}
                    onChange={(e) => setNewStrategy({ ...newStrategy, broadcastOnPercentileChange: e.target.checked })}
                    style={{ marginRight: '10px' }}
                  />
                  <span style={{ color: '#f1f5f9', fontSize: '14px' }}>Broadcast on Percentile Change (&gt;0.5%)</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={newStrategy.broadcastOnVolatilityChange}
                    onChange={(e) => setNewStrategy({ ...newStrategy, broadcastOnVolatilityChange: e.target.checked })}
                    style={{ marginRight: '10px' }}
                  />
                  <span style={{ color: '#f1f5f9', fontSize: '14px' }}>Broadcast on Volatility Regime Change</span>
                </label>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#94a3b8', fontSize: '14px' }}>
                  Notes (Optional)
                </label>
                <textarea
                  value={newStrategy.notes}
                  onChange={(e) => setNewStrategy({ ...newStrategy, notes: e.target.value })}
                  placeholder="Optional notes about this subscription"
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#0f172a',
                    border: '1px solid #334155',
                    borderRadius: '6px',
                    color: '#f1f5f9',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
              </div>
              <button
                type="submit"
                style={{
                  padding: '12px 32px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Create Subscription
              </button>
            </form>
          </div>
        )}

        {/* Subscriptions List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
            Loading subscriptions...
          </div>
        ) : error ? (
          <div style={{
            padding: '20px',
            backgroundColor: '#7f1d1d',
            color: '#fca5a5',
            borderRadius: '8px',
            border: '1px solid #991b1b'
          }}>
            Error: {error}
          </div>
        ) : subscriptions.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px',
            color: '#94a3b8',
            backgroundColor: '#1e293b',
            borderRadius: '12px',
            border: '1px solid #334155'
          }}>
            <p style={{ fontSize: '48px', marginBottom: '16px' }}>📭</p>
            <p style={{ fontSize: '18px', marginBottom: '8px' }}>No subscriptions yet</p>
            <p style={{ fontSize: '14px' }}>Click "Add Subscription" to create your first strategy subscription</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '16px' }}>
            {subscriptions.map((subscription) => (
              <div
                key={subscription.id}
                style={{
                  backgroundColor: '#1e293b',
                  borderRadius: '12px',
                  padding: '20px',
                  border: `2px solid ${subscription.enabled ? '#10b981' : '#475569'}`,
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                      <h3 style={{
                        fontSize: '20px',
                        color: subscription.enabled ? '#10b981' : '#94a3b8',
                        margin: 0
                      }}>
                        {subscription.strategyName}
                      </h3>
                      <span style={{
                        padding: '4px 12px',
                        backgroundColor: subscription.enabled ? '#065f46' : '#475569',
                        color: subscription.enabled ? '#d1fae5' : '#cbd5e1',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {subscription.enabled ? 'ACTIVE' : 'DISABLED'}
                      </span>
                      {subscription.canBroadcastNow && subscription.enabled && (
                        <span style={{
                          padding: '4px 12px',
                          backgroundColor: '#1e40af',
                          color: '#bfdbfe',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          READY
                        </span>
                      )}
                    </div>

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '16px',
                      marginTop: '16px'
                    }}>
                      <div>
                        <p style={{ color: '#64748b', fontSize: '12px', margin: '0 0 4px 0' }}>Broadcast Count</p>
                        <p style={{ color: '#f1f5f9', fontSize: '16px', fontWeight: 'bold', margin: 0 }}>
                          {subscription.broadcastCount}
                        </p>
                      </div>
                      <div>
                        <p style={{ color: '#64748b', fontSize: '12px', margin: '0 0 4px 0' }}>Last Broadcast</p>
                        <p style={{ color: '#f1f5f9', fontSize: '14px', margin: 0 }}>
                          {formatTimestamp(subscription.lastBroadcastAt)}
                        </p>
                      </div>
                      <div>
                        <p style={{ color: '#64748b', fontSize: '12px', margin: '0 0 4px 0' }}>Next Available</p>
                        <p style={{ color: '#f1f5f9', fontSize: '14px', margin: 0 }}>
                          {getNextBroadcastTime(subscription)}
                        </p>
                      </div>
                      <div>
                        <p style={{ color: '#64748b', fontSize: '12px', margin: '0 0 4px 0' }}>Throttle</p>
                        <p style={{ color: '#f1f5f9', fontSize: '14px', margin: 0 }}>
                          {subscription.throttleMinutes} min
                        </p>
                      </div>
                    </div>

                    <div style={{ marginTop: '12px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      {subscription.broadcastOnPercentileChange && (
                        <span style={{
                          fontSize: '12px',
                          color: '#94a3b8',
                          padding: '4px 8px',
                          backgroundColor: '#0f172a',
                          borderRadius: '4px'
                        }}>
                          ✓ Percentile Changes
                        </span>
                      )}
                      {subscription.broadcastOnVolatilityChange && (
                        <span style={{
                          fontSize: '12px',
                          color: '#94a3b8',
                          padding: '4px 8px',
                          backgroundColor: '#0f172a',
                          borderRadius: '4px'
                        }}>
                          ✓ Volatility Regime
                        </span>
                      )}
                    </div>

                    {subscription.notes && (
                      <p style={{
                        color: '#94a3b8',
                        fontSize: '13px',
                        marginTop: '12px',
                        fontStyle: 'italic'
                      }}>
                        {subscription.notes}
                      </p>
                    )}
                  </div>

                  <button
                    onClick={() => handleToggleEnabled(subscription)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: subscription.enabled ? '#dc2626' : '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = subscription.enabled ? '#b91c1c' : '#059669';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = subscription.enabled ? '#dc2626' : '#10b981';
                    }}
                  >
                    {subscription.enabled ? 'Disable' : 'Enable'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StrategySubscriptions;
